import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Calendar, Tag } from 'lucide-react';
import { formatPostDate } from '../../constants/blogPosts';
import { PHONE_NUMBER, PHONE_TEL_HREF } from '../../constants/site';
import { PostCard } from './BlogLayout';

const VideoBlock = ({ video }) => (
  <figure className="my-6" data-testid="post-video">
    <div className="mx-auto max-w-sm rounded-2xl overflow-hidden border-2 border-gold-400/60 shadow-2xl bg-black">
      <video controls playsInline preload="none" poster={video.poster} className="w-full h-auto" aria-label={video.caption}>
        <source src={video.src} type="video/mp4" />
      </video>
    </div>
    <figcaption className="text-center text-sm text-neutral-500 mt-3">{video.caption}</figcaption>
  </figure>
);

const CtaBlock = ({ text }) => (
  <div className="mt-8 bg-[#143160] text-white rounded-2xl p-8 text-center space-y-4" data-testid="post-cta">
    <p className="font-serif text-2xl sm:text-3xl">{text}</p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <a href={PHONE_TEL_HREF} className="inline-flex items-center justify-center bg-gold-500 hover:bg-gold-400 text-[#143160] font-black px-6 py-3 rounded-md"><Phone className="w-4 h-4 mr-2" />{PHONE_NUMBER}</a>
      <Link to="/home-value" className="inline-flex items-center justify-center border-2 border-white/40 hover:border-gold-400 font-bold px-6 py-3 rounded-md">Get My Free Home Value</Link>
    </div>
  </div>
);

export const PostBlock = ({ block, post }) => {
  switch (block.type) {
    case 'h2': return <h2 className="font-serif text-3xl text-[#143160] pt-4">{block.text}</h2>;
    case 'video': return <VideoBlock video={post.video} />;
    case 'cta': return <CtaBlock text={block.text} />;
    default: return <p className="text-lg leading-relaxed text-neutral-700">{block.text}</p>;
  }
};

export const PostHeader = ({ post }) => (
  <>
    <p className="text-xs font-bold tracking-[0.25em] uppercase text-gold-600 mb-3">{post.category}</p>
    <h1 data-testid="post-h1" className="font-serif text-4xl sm:text-5xl text-[#143160] leading-[1.1]">{post.title}</h1>
    {post.subtitle && <p className="mt-3 text-xl text-neutral-600 font-semibold">{post.subtitle}</p>}
    <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500 mt-5 pb-6 border-b border-[#143160]/10">
      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatPostDate(post.date)}</span>
      <span>By Jessica Burnette, REALTOR®</span>
      <span>{post.readTime}</span>
    </div>
  </>
);

export const PostTags = ({ tags }) => (
  <div className="flex flex-wrap gap-2 pt-8">
    {tags.map((t) => <span key={t} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-gold-100 text-[#143160]"><Tag className="w-3 h-3" />{t}</span>)}
  </div>
);

export const MorePosts = ({ posts }) => {
  if (posts.length === 0) return null;
  return (
    <section className="max-w-5xl mx-auto px-5 pt-16">
      <h2 className="font-serif text-3xl text-[#143160] mb-6">More from the blog</h2>
      <div className="grid sm:grid-cols-2 gap-6">{posts.map((p) => <PostCard key={p.slug} post={p} />)}</div>
    </section>
  );
};
